import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ts = (iso) => (iso ? `${iso.slice(0, 16).replace("T", " ")} UTC` : "none");

const Row = ({ label, children, testId }) => (
  <div className="flex flex-wrap gap-x-3 border-b border-white/5 py-2 text-sm last:border-0">
    <dt className="w-full shrink-0 text-xs text-stone-400 sm:w-48 sm:text-sm">{label}</dt>
    <dd className="min-w-0 flex-1 break-words text-stone-200" data-testid={testId}>{children}</dd>
  </div>
);

const stateOf = (o) => {
  if (o.active) return o.stale ? ["Stale", "border-red-400/40 bg-red-400/10 text-red-200"] : ["Active", "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"];
  if (!o.config?.configured) return ["Not configured", "border-stone-400/30 bg-white/5 text-stone-300"];
  return ["Configured, uploads OFF", "border-amber-400/40 bg-amber-400/10 text-amber-100"];
};

const KeyCheck = ({ k }) => (
  <span>
    {k.restricted_to_bucket ? "Restricted to this bucket" : `NOT restricted (buckets: ${(k.buckets ?? []).join(", ") || "all"})`}
    {" · "}{(k.capabilities ?? []).join(", ")}
    {k.has_delete && <strong className="text-amber-200"> · has deleteFiles (not needed)</strong>}
    {(k.missing_required ?? []).length > 0 && <strong className="text-red-300"> · missing {k.missing_required.join(", ")}</strong>}
    {" · lifecycle rules: "}{k.lifecycle_rules == null ? "unreadable" : k.lifecycle_rules.length ? k.lifecycle_rules.map((r) => `${r.fileNamePrefix || "(all)"} hide ${r.daysFromUploadingToHiding ?? "-"}d / delete ${r.daysFromHidingToDeleting ?? "-"}d`).join("; ") : "none set"}
    {" · checked "}{ts(k.at)}
  </span>
);

export const OffsiteStatus = ({ o, newest, reload }) => {
  const [busy, setBusy] = useState("");
  const [state, cls] = stateOf(o);
  const act = async (key, fn) => { setBusy(key); try { await fn(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); reload(); } };
  const btn = (key, label, fn, disabled) => (
    <button type="button" disabled={!o.active || !!busy || disabled} onClick={() => act(key, fn)} data-testid={`offsite-${key}-btn`}
      className="inline-flex h-9 items-center gap-2 rounded-full border border-white/15 px-4 text-xs disabled:opacity-40">
      {busy === key && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{label}</button>
  );
  const up = o.last_upload, rt = o.last_restore_test, f = o.last_failure;
  return (
    <section className="rounded-2xl border border-white/10 bg-[#141110] p-5" data-testid="offsite-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-display text-lg">Off-site backups (Backblaze B2)</h3>
        <span className={`rounded-full border px-3 py-0.5 text-xs ${cls}`} data-testid="offsite-state">{state}</span>
      </div>
      <dl className="mt-3">
        <Row label="Bucket / prefix" testId="offsite-target">{o.config?.bucket || "unset"} / {o.config?.prefix || "unset"}/ · {o.config?.region || "-"}</Row>
        {(o.missing ?? []).length > 0 && <Row label="Settings still needed" testId="offsite-missing">{o.missing.join(", ")}</Row>}
        <Row label="Last verified upload" testId="offsite-last-upload">{up ? `${ts(up.at)} · ${up.file} · ${(up.size_bytes / 1024).toFixed(1)} KB · SHA-256 re-download check ${up.verified ? "passed" : "failed"}` : "none"}</Row>
        <Row label="Off-site restore test" testId="offsite-last-restore">{rt ? <span className={rt.success ? "text-emerald-300" : "text-red-300"}>{rt.success ? "passed" : "FAILED"} · {ts(rt.at)} · temporary database dropped</span> : "none"}</Row>
        <Row label="Key permissions" testId="offsite-key-check">{o.last_key_check ? <KeyCheck k={o.last_key_check} /> : "not checked yet"}</Row>
        <Row label="Last failure" testId="offsite-last-failure">{f ? <span className="text-red-300">{ts(f.at)} · {f.stage}: {f.reason}</span> : "none"}</Row>
        <Row label="Failure alerts" testId="offsite-alert-email">{o.config?.alert_email || "unset"}</Row>
      </dl>
      {o.stale && <p className="mt-3 text-sm text-red-300" data-testid="offsite-stale">No verified off-site backup in the last 26 hours.</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {btn("key-check", "Check key permissions", async () => { await api.post("/admin/backups/offsite/key-check"); toast.success("Key checked"); })}
        {btn("upload", "Upload newest backup", async () => { await api.post(`/admin/backups/${newest}/offsite-upload`); toast.success("Uploaded and verified"); }, !newest)}
        {btn("restore", "Off-site restore test", async () => { const r = await api.post("/admin/backups/offsite/restore-test"); toast[r.data?.success ? "success" : "error"](r.data?.success ? "Off-site restore test passed" : "Off-site restore test failed"); })}
      </div>
      {!o.active && <p className="mt-3 text-xs text-stone-400" data-testid="offsite-inactive-note">Off-site actions stay disabled until the owner approves the first real upload.</p>}
    </section>
  );
};
